import type { Pumps } from "gql/api";
import type { EquipmentTableColumn } from "@/composables/equipment/equipmentTypes";
import { computePumpStageType } from "@/models/equipment/pumps/properties";

/** Pumps plus virtual/computed columns (e.g. pump_efficiency, required_pump_stages) shown in the table; row may be null in view */
export type PumpsRow = Pumps & {
  pump_efficiency?: number | null;
  /** Required stages if this pump were used in the current scenario (from hypothetical calcs in pump search). */
  required_pump_stages?: number | null;
};

export const pumpTableColumns: EquipmentTableColumn<PumpsRow>[] = [
  {
    editable: true,
    field: "include_koc_timestamp",
    header: "Include KOC",
    sortable: true,
    isCheckbox: true,
    adminOnly: true
  },
  {
    editable: true,
    field: "only_koc_timestamp",
    header: "KOC Only",
    sortable: true,
    isCheckbox: true,
    adminOnly: true
  },
  {
    editable: true,
    field: "include_ecopetrol_timestamp",
    header: "Include Ecopetrol",
    sortable: true,
    isCheckbox: true,
    adminOnly: true
  },
  {
    editable: true,
    field: "only_ecopetrol_timestamp",
    header: "Ecopetrol Only",
    sortable: true,
    isCheckbox: true,
    adminOnly: true
  },
  {
    editable: true,
    field: "manufacturer",
    header: "Manufacturer",
    frozen: true,
    searchable: true
  },
  {
    editable: true,
    field: "series_model",
    header: "Pump Series/Model",
    frozen: true,
    searchable: true
  },
  {
    editable: true,
    field: "part_number",
    header: "Part Number",
    sortable: true,
    adminOnly: true,
    searchable: true
  },
  {
    editable: true,
    field: "stage_type",
    header: "Stage Type",
    isNumber: true,
    sortable: true,
    adminOnly: true,
    format: (value: unknown) =>
      computePumpStageType({ stage_type: value as number | null })
  },
  {
    editable: true,
    field: "rotation",
    header: "Rotation",
    sortable: true,
    searchable: true
  },
  {
    editable: true,
    field: "compression_thrust_per_stage",
    header: "Compression Thrust Per Stage",
    isNumber: true,
    sortable: true,
    adminOnly: true,
    class: "extra-wide-column"
  },
  {
    editable: true,
    field: "shaft_diameter_inches",
    header: "Shaft Diameter (in)",
    isNumber: true,
    sortable: true,
    adminOnly: true,
    searchable: true,
    decimalPlaces: 6
  },
  {
    editable: true,
    field: "housing_outer_diameter_inches",
    header: "Outer Diameter (in)",
    isNumber: true,
    sortable: true,
    searchable: true,
    decimalPlaces: 6
  },
  {
    editable: true,
    field: "housing_inner_diameter_inches",
    header: "Inner Diameter (in)",
    isNumber: true,
    sortable: true,
    searchable: true,
    decimalPlaces: 6,
    adminOnly: true
  },
  {
    editable: true,
    field: "neck_outer_diameter_inches",
    header: "Neck Outer Diameter (in)",
    isNumber: true,
    sortable: true,
    searchable: true,
    decimalPlaces: 6,
    adminOnly: true
  },
  {
    editable: true,
    field: "neck_inner_diameter_inches",
    header: "Neck Inner Diameter (in)",
    isNumber: true,
    sortable: true,
    searchable: true,
    decimalPlaces: 6,
    adminOnly: true
  },
  {
    editable: true,
    field: "standard_housing_pressure_rating_psi",
    header: "Standard Housing Pressure Rating (psi)",
    isNumber: true,
    sortable: true,
    adminOnly: true,
    searchable: true
  },
  {
    editable: true,
    field: "high_strength_housing_pressure_rating_psi",
    header: "High Strength Housing Pressure Rating (psi)",
    isNumber: true,
    sortable: true,
    adminOnly: true,
    searchable: true
  },
  {
    editable: true,
    field: "standard_shaft_rating_hp",
    header: "Standard Shaft Rating (hp)",
    isNumber: true,
    sortable: true,
    adminOnly: true,
    searchable: true
  },
  {
    editable: true,
    field: "high_strength_shaft_rating_hp",
    header: "High Strength Shaft Rating (hp)",
    isNumber: true,
    sortable: true,
    adminOnly: true,
    searchable: true
  },
  {
    editable: true,
    field: "construction",
    header: "Construction",
    sortable: true,
    adminOnly: true
  },
  {
    editable: true,
    field: "min_rate_standard_pump_bpd",
    header: "Min Rate (BPD 60hz)",
    isNumber: true,
    sortable: true
  },
  {
    editable: true,
    field: "bep_rate_standard_pump_bpd",
    header: "BEP Rate (BPD 60hz)",
    isNumber: true,
    sortable: true
  },
  {
    editable: true,
    field: "max_rate_standard_pump_bpd",
    header: "Max Rate (BPD 60hz)",
    isNumber: true,
    sortable: true
  },
  {
    editable: false,
    field: "pump_efficiency",
    header: "Pump Efficiency",
    isNumber: true,
    sortable: true,
    format: (value: unknown) => {
      if (value === null || value === undefined) return "—";
      const efficiency = value as number;
      return `${(efficiency * 100).toFixed(1)}%`;
    }
  },
  {
    editable: false,
    field: "required_pump_stages",
    header: "Required Stages",
    isNumber: true,
    sortable: true,
    format: (value: unknown) => {
      if (value === null || value === undefined) return "—";
      const n = value as number;
      return Number.isFinite(n) ? String(Math.round(n)) : "—";
    }
  },
  {
    editable: true,
    field: "max_graph_rate_standard_pump_bpd",
    header: "Max Graph Rate (BPD)",
    isNumber: true,
    sortable: true,
    adminOnly: true
  },
  {
    editable: true,
    field: "max_bolt_load_pounds",
    header: "Max Bolt Load (lbs)",
    isNumber: true,
    sortable: true,
    decimalPlaces: 6,
    adminOnly: true
  },
  {
    editable: true,
    field: "hb",
    header: "HB",
    isNumber: true,
    sortable: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    field: "h1",
    header: "H1",
    isNumber: true,
    sortable: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    field: "h2",
    header: "H2",
    isNumber: true,
    sortable: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    field: "h3",
    header: "H3",
    isNumber: true,
    sortable: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    field: "h4",
    header: "H4",
    isNumber: true,
    sortable: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    field: "h5",
    header: "H5",
    isNumber: true,
    sortable: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    field: "bhpb",
    header: "BHPB",
    isNumber: true,
    sortable: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    field: "bhp1",
    header: "BHP1",
    isNumber: true,
    sortable: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    field: "bhp2",
    header: "BHP2",
    isNumber: true,
    sortable: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    field: "bhp3",
    header: "BHP3",
    isNumber: true,
    sortable: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    field: "bhp4",
    header: "BHP4",
    isNumber: true,
    sortable: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    field: "bhp5",
    header: "BHP5",
    isNumber: true,
    sortable: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    field: "npsh1",
    header: "NPSH1",
    isNumber: true,
    sortable: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    field: "npsh2",
    header: "NPSH2",
    isNumber: true,
    sortable: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    field: "npsh3",
    header: "NPSH3",
    isNumber: true,
    sortable: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    field: "npsh4",
    header: "NPSH4",
    isNumber: true,
    sortable: true,
    adminOnly: true,
    decimalPlaces: 25
  },
  {
    editable: true,
    field: "is_compression_pump",
    header: "Is Compression Pump",
    isDropdown: true,
    sortable: true,
    adminOnly: true,
    searchable: true
  }
];
